import { FC, useCallback, useEffect, useState } from "react"
import { Formik, useFormikContext } from "formik"
import { WasmRunner } from "@coherentglobal/wasm-runner"
import { ResponseObject } from "@coherentglobal/wasm-runner/dist/types"

import { CalculationSlideout } from "../../components/dashboard/CalculationSlideout"
import { FormValues } from "../../types"
import { DashboardForm } from "./DashboardForm"
import { formatFormValues, formatRequestData } from "../../utils/dataFormat"
import { InputJson } from "./InputJson"
import { prepareEditableJsonData } from "../../utils/dataFormat"
import { isEqual, debounce } from "lodash"
import { ExcelIcon } from "../../icons/ExcelIcon"
import { IntegrateSection } from "./IntegrateSection"
// import ApiClient from "../../api/ApiClient"

export type DashboardTabProps = {
  /**
   * Existing request data used to repopulate the form after switching tabs
   */
  request?: any
  /**
   * Existing response data used to display monthly payment
   */
  response?: any
  /**
   * Save the request so it can be used in the output tab
   */
  setRequest: (value: any) => void
  /**
   * Save the response so it can be used in the output tab
   */
  setResponse: (value: ResponseObject | { error: any }) => void
  /**
   * Function used to switch between tabs
   */
  setTabIndex: (value: number) => void
  /**
   * Wasm runner initializer function
   */
  initWasmRunner: () => void
  /**
   * Wasm runner
   */
  runner: WasmRunner
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SubmitListener: FC<{ runner: WasmRunner }> = ({ runner }) => {
  const formik = useFormikContext()
  const [lastValues, updateState] = useState(formik.values)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const submitForm = useCallback(
    debounce(
      (): void => {
        formik.submitForm()
      },
      500,
      { maxWait: 1500 }
    ),
    []
  )

  useEffect(() => {
    if (runner) {
      formik.submitForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runner])

  useEffect(() => {
    const valuesEqualLastValues = isEqual(lastValues, formik.values)
    const valuesEqualInitialValues = isEqual(formik.values, formik.initialValues)

    if (!valuesEqualLastValues) {
      updateState(formik.values)
    }

    if (!valuesEqualLastValues && !valuesEqualInitialValues && formik.isValid) {
      submitForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values, formik.isValid])

  return null
}

export const DashboardTab: FC<DashboardTabProps> = ({
  request = null,
  response = null,
  setRequest,
  setResponse,
  setTabIndex,
  initWasmRunner,
  runner,
}) => {
  const [jsonView, setJsonView] = useState<boolean>(false)
  const [editableModelData, setEditableModelData] = useState<any>(prepareEditableJsonData())
  const [isSlideoutOpen, setIsSlideoutOpen] = useState<boolean>(false)

  const runCalculation = async (payload: any) => {
    if (runner) {
      return await runner.execute(payload).catch((err) => ({ error: err.v3 }))
    } else {
      await initWasmRunner()
      console.error("err")

      throw Error("Runner not initialized. Please retry.")
    }
  }

  const calculate = async (values: any) => {
    const response = await runCalculation(formatRequestData(values))
    setRequest(values)
    setResponse(response)
    setIsSlideoutOpen(true)
  }

  const calculateJSON = async (values: any) => {
    const response = await runCalculation(values)
    setRequest(values)
    setResponse(response)
    setIsSlideoutOpen(true)
  }

  return (
    <>
      <div className="h-24 w-full flex items-center justify-center">
        <h1 className="pt-8 w-full max-w-xs text-center text-black text-3xl font-semibold">401K Calculator</h1>
      </div>

      <div className="flex mx-auto w-full max-w-sm">
        <button
          className={`${!jsonView && "border-black"} border-b py-2 text-sm w-1/2`}
          type="button"
          onClick={() => setJsonView(false)}
        >
          FIELDS
        </button>
        <button
          className={`${jsonView && "border-black"} border-b py-2 text-sm w-1/2`}
          type="button"
          onClick={() => setJsonView(true)}
        >
          FOR DEVS
        </button>
      </div>

      <Formik
        initialValues={request ? request : editableModelData.request_data.inputs}
        onSubmit={(values: FormValues) => {
          const formattedValues = formatFormValues(values)
          calculate(formattedValues)
        }}
      >
        {({ errors, touched, values, handleChange, setFieldValue, resetForm }) => (
          <>
            {jsonView ? (
              <div className="max-w-sm w-full mx-auto">
                <div className="bg-white rounded-lg p-4 h-[276px] flex flex-col space-y-4">
                  <div className="flex">
                    <div className="rounded-full text-center bg-[#F3E7FF] text-brand-light h-7 w-7 leading-7 mr-1">
                      1
                    </div>
                    <span className="font-bold">Upload your spreadsheets to Spark Coherent</span>
                  </div>
                  <div className="bg-form-medium h-full flex items-center justify-center">
                    <ExcelIcon className="text-brand-dark" />
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 flex flex-col space-y-4 mt-4">
                  <div className="flex">
                    <div className="rounded-full text-center bg-[#F3E7FF] text-brand-light h-7 w-7 leading-7 mr-1">
                      2
                    </div>
                    <span className="font-bold">Convert your spreadsheets into ready-to-integrate APIs</span>
                  </div>
                  <InputJson
                    height="16rem"
                    calculate={calculateJSON}
                    defaultValues={editableModelData}
                    setDefaultFormValues={setEditableModelData}
                    resetDefaultValues={() => setEditableModelData(prepareEditableJsonData())}
                    removePadding
                  />
                </div>

                <div className="bg-white rounded-lg p-4 mt-4 mb-8">
                  <div className="flex">
                    <div className="rounded-full text-center bg-[#F3E7FF] text-brand-light h-7 w-7 leading-7 mr-1">
                      3
                    </div>
                    <span className="font-bold">Integrate with virtually any platform on the market</span>
                  </div>
                  <IntegrateSection />
                </div>
              </div>
            ) : (
              <>
                {/* uncomment this component if recalc on every field change is needed */}
                {/* <SubmitListener runner={runner} /> */}
                <DashboardForm
                  responseData={response ? response.response_data : null}
                  errors={errors}
                  touched={touched}
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setTabIndex={setTabIndex}
                  resetForm={resetForm}
                />
              </>
            )}

            <CalculationSlideout
              open={isSlideoutOpen}
              setOpen={setIsSlideoutOpen}
              request={request}
              response={response}
              shareFunction={() => {}}
              resetFunction={() => {
                setIsSlideoutOpen(false)
                resetForm()
              }}
            />
          </>
        )}
      </Formik>
    </>
  )
}

